import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ChangeDetectorRef,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';
import { ApplicationService } from '../../services/application.service';
import { AuthService } from '../../services/auth.service';
import { TranslationService } from '../../services/translation.service';
import type { ApplicationSummaryCardTranslations } from '../../i18n/application-summary-card';
import en from '../../i18n/en/application-summary-card';
import {
  ApplicationStatus,
  APPLICATION_STATUSES,
} from '../../models/application-status.model';
import { Application } from '../../models/application.model';
import { parseUtcTimestamp } from '../../utils/elapsed-time.util';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { CoverLetterTableComponent } from '../cover-letter-table/cover-letter-table.component';
import { CurriculumVitaeTableComponent } from '../curriculum-vitae-table/curriculum-vitae-table.component';
import { MenuModule } from 'primeng/menu';
import { CurriculumVitaeService } from '../../services/curriculum-vitae.service';
import { CurriculumVitaeResponse } from '../../models/curriculum-vitae.model';
import { CoverLetterService } from '../../services/cover-letter.service';
import { CoverLetterResponse } from '../../models/cover-letter.model';
import { JobPostingResponse } from '../../models/job-posting.model';
import { JobPostingService } from '../../services/job-posting.service';
import { MessageService } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressBarModule } from 'primeng/progressbar';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { FollowUpComponent } from '../follow-up/follow-up.component';
import { JobSalaryComponent } from '../job-salary/job-salary.component';
import { JobInvitationComponent } from '../job-invitation/job-invitation.component';
import { JobApplicationWizardServiceService } from '../../services/job-application-wizard-service.service';
import { CurrencyFormatService } from '../../services/currency-format.service';
import { CoverLettersService } from '../../services/cover-letters.service';
import { ApplicationLabelsComponent } from '../application-labels/application-labels.component';
import { TimingMetricsService } from '../../services/timing-metrics.service';
import { ElapsedTimerComponent } from '../shared/elapsed-timer/elapsed-timer.component';

@Component({
  selector: 'app-application-summary-card',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatMenuModule,
    MatIconModule,
    MatButtonModule,
    CoverLetterTableComponent,
    CurriculumVitaeTableComponent,
    MenuModule,
    TooltipModule,
    ProgressBarModule,
    ToastModule,
    DialogModule,
    FollowUpComponent,
    JobSalaryComponent,
    JobInvitationComponent,
    ApplicationLabelsComponent,
    ElapsedTimerComponent,
  ],
  templateUrl: './application-summary-card.component.html',
  styleUrls: ['./application-summary-card.component.css'],
})
export class ApplicationSummaryCardComponent implements OnChanges, OnDestroy {
  /** 'table' (dense row, inside applications-table's grid) or 'card' (the
   * 2-column card grid) - both render the exact same Status/Match/Salary/
   * Location/Timing/Actions content via shared ng-templates in the html,
   * just arranged in a different outer container, so the two view modes
   * can never visually drift out of sync with each other. */
  @Input() viewMode: 'table' | 'card' = 'table';

  translations: ApplicationSummaryCardTranslations = en;
  items: any[] = [];

  /** Gates the speed-dial "Delete" action to admin/super_admin users only. */
  isAdmin = false;
  private subscription = new Subscription();

  showFollowUpDialog = false;
  isSubmittingFollowUp = false;

  showSalaryDialog = false;
  isSubmittingSalary = false;

  showInvitationDialog = false;

  openInvitationDialog() {
    this.showInvitationDialog = true;
  }

  closeInvitationDialog() {
    this.showInvitationDialog = false;
  }

  openFollowUpDialog() {
    this.showFollowUpDialog = true;
  }

  closeFollowUpDialog() {
    this.showFollowUpDialog = false;
  }

  openSalaryDialog() {
    this.showSalaryDialog = true;
  }

  onLabelsChanged(labels: any[]): void {
    if (this.applicationDetails) {
      this.applicationDetails = { ...this.applicationDetails, labels };
    }
    if (this.application) {
      this.application = { ...this.application, labels };
    }
    this.cdr.markForCheck();
  }

  closeSalaryDialog() {
    this.showSalaryDialog = false;
    this.cdr.markForCheck();
  }

  handleSalaryUpdated(data: any) {
    if (this.applicationDetails) {
      this.applicationDetails = {
        ...this.applicationDetails,
        salary_min_value:
          data?.salary_min_value ?? this.applicationDetails.salary_min_value ?? null,
        salary_max_value:
          data?.salary_max_value ?? this.applicationDetails.salary_max_value ?? null,
        salary_target_value:
          data?.salary_target_value ?? this.applicationDetails.salary_target_value ?? null,
        salary_company_message:
          data?.salary_company_message ??
          this.applicationDetails.salary_company_message ??
          null,
      };
    }

    if (this.application) {
      this.application = {
        ...this.application,
        salary_min_value: data?.salary_min_value ?? this.application.salary_min_value,
        salary_max_value: data?.salary_max_value ?? this.application.salary_max_value,
        salary_target_value:
          data?.salary_target_value ?? this.application.salary_target_value,
        salary_company_message:
          data?.salary_company_message ?? this.application.salary_company_message,
      };
    }

    this.cdr.markForCheck();
    this.closeSalaryDialog();
    this.messageService.add({
      severity: 'success',
      summary: this.t.TOAST_SUCCESS,
      detail: this.t.TOAST_SALARY_UPDATED,
      life: 3000,
    });
  }

  handleFollowUpSubmit(data: any) {
    if (!this.applicationDetails?.id) return;
    this.isSubmittingFollowUp = true;
    this.applicationService
      .logFollowUp(this.applicationDetails.id, data)
      .subscribe({
        next: () => {
          this.isSubmittingFollowUp = false;
          this.closeFollowUpDialog();
          this.messageService.add({
            severity: 'success',
            summary: this.t.TOAST_SUCCESS,
            detail: this.t.TOAST_FOLLOW_UP_LOGGED,
            life: 3000,
          });
        },
        error: (err) => {
          this.isSubmittingFollowUp = false;
          this.messageService.add({
            severity: 'error',
            summary: this.t.TOAST_ERROR,
            detail: this.t.TOAST_FOLLOW_UP_FAILED,
            life: 3000,
          });
        },
      });
  }

  /** Backend timestamps are naive UTC ISO strings with no offset suffix -
   * see elapsed-time.util.ts::parseUtcTimestamp()'s own doc comment for
   * why a bare `new Date(isoString)` on one of these is wrong (utils/
   * date-utils.ts::timeAgo() shared this same pattern, fixed in
   * chamba-ai#320 along with every other frontend call site of the same
   * bug class). Only used for the live pre-submission ticker; the
   * persisted time_to_submit_seconds KPI is computed entirely server-side
   * and is unaffected by any client timezone ambiguity here. */
  /** Snapshotted ONCE per fetch/reload, never a live ticker - this card
   * renders in a plain @for loop (applications-table.component.html) with
   * no virtualization, so a user with hundreds of applications would
   * otherwise get hundreds of concurrent setInterval-driven timer
   * instances at once, which is exactly what caused a real reported
   * performance hang. A single-application context (the wizard header,
   * job-application-wizard.component.ts) is the only place a live
   * ticker is appropriate - never here. */
  private computeTimeSoFarSeconds(isoString: string): number {
    // parseUtcTimestamp, not a bare `new Date(isoString)`: found live
    // comparing this card's own Timing block rows against each other -
    // see elapsed-time.util.ts's own doc comment for the full story
    // (the frontend sibling of the RG-37/chamba-ai-backend-fastapi#186
    // clock-mismatch bug class).
    return Math.floor((Date.now() - parseUtcTimestamp(isoString).getTime()) / 1000);
  }

  onEdit(): void {
    // TODO: Implement edit logic or open edit dialog
    console.log('Edit action triggered');
  }

  @Input() application: Application | null = null;
  @Input() applicationId: number | null = null;
  @Input() refreshTrigger: number = 0; // Increment to force re-fetch
  applicationDetails: {
    id: number;
    status: ApplicationStatus;
    job_title: string;
    company_name: string;
    url: string;
    user_id: number;
    job_description_language: string;
    run_costs: number;
    created_at: string;
    cv_available: boolean;
    cover_letter_available: boolean;
    job_posting_available: boolean;
    total_progress: number;
    lead_time_seconds: number | null;
    time_to_submit_seconds?: number | null;
    first_cv_version_created_at?: string | null;
    // Computed once in setApplicationDetails(), never a live ticker - see
    // computeTimeSoFarSeconds()'s comment.
    time_so_far_seconds?: number | null;
    // chamba-ai-backend-fastapi#220
    time_to_first_response_seconds?: number | null;
    submitted_at?: string | null;
    time_since_submission_so_far_seconds?: number | null;
    company_address: string;
    last_follow_up: string;
    profile_matching_score?: number | null;
    salary_min_value?: number | null;
    salary_max_value?: number | null;
    salary_target_value?: number | null;
    salary_company_message?: string | null;
    labels?: { id: number; name: string; color: string; is_standard: boolean; i18n_key: string | null }[];
  } | null = null;
  isLoading: boolean = false;
  error: string | null = null;
  showCoverLetterTable: boolean = false;
  showCurriculumVitaeTable: boolean = false;

  // Status configuration
  readonly statuses = APPLICATION_STATUSES;
  readonly statusOptions = Object.keys(
    APPLICATION_STATUSES
  ) as ApplicationStatus[];

  public activateGooogleMaps = environment.activateGooogleMaps;

  private setApplicationDetails(data: any): void {
    if (!data) {
      this.applicationDetails = null;
      return;
    }
    this.applicationDetails = {
      ...data,
      status: data.status as ApplicationStatus,
      time_so_far_seconds: data.first_cv_version_created_at
        ? this.computeTimeSoFarSeconds(data.first_cv_version_created_at)
        : null,
      // chamba-ai-backend-fastapi#220: mirrors time_so_far_seconds above -
      // a live "waiting so far" value before a response arrives, computed
      // once here (not a live ticker, same rationale as
      // computeTimeSoFarSeconds()'s own comment) from submitted_at.
      time_since_submission_so_far_seconds: data.submitted_at
        ? this.computeTimeSoFarSeconds(data.submitted_at)
        : null,
    };
  }

  constructor(
    private applicationService: ApplicationService,
    private curriculumVitaeService: CurriculumVitaeService,
    private coverLetterService: CoverLetterService,
    private jobPostingService: JobPostingService,
    private cdr: ChangeDetectorRef,
    private messageService: MessageService,
    private jobApplicationWizardService: JobApplicationWizardServiceService,
    private sanitizer: DomSanitizer,
    public currencyFormat: CurrencyFormatService,
    private coverLettersApiService: CoverLettersService,
    private translationService: TranslationService,
    private authService: AuthService,
    protected timingMetricsService: TimingMetricsService
  ) {
    const current = this.translationService.getApplicationSummaryCardTranslations();
    if (current) {
      this.translations = current;
    }
    this.timingMetricsService.ensureLoaded();
    this.rebuildItems();

    this.translationService.onChange(() => {
      const next = this.translationService.getApplicationSummaryCardTranslations();
      if (next) {
        this.translations = next;
        this.rebuildItems();
        this.cdr.markForCheck();
      }
    });

    this.subscription.add(
      this.authService.isAdmin$.subscribe((isAdmin) => {
        this.isAdmin = isAdmin;
        this.rebuildItems();
        this.cdr.markForCheck();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  get t(): ApplicationSummaryCardTranslations {
    return this.translations;
  }

  private interpolate(
    template: string,
    params: Record<string, string | number>
  ): string {
    return Object.keys(params).reduce(
      (acc, key) => acc.replaceAll(`{${key}}`, String(params[key])),
      template
    );
  }

  private rebuildItems(): void {
    this.items = [
      // {
      //   label: this.t.SPEEDDIAL_EDIT,
      //   icon: 'bi bi-pencil',
      //   command: () => this.onEdit(),
      //   iconColor: 'icon-blue',
      // },
      // Admin-only: a regular user deleting their own application is not
      // a supported action in this UI (no "are you sure" recovery path),
      // so this destructive action is restricted to admin/super_admin.
      ...(this.isAdmin
        ? [
            {
              label: this.t.SPEEDDIAL_DELETE,
              icon: 'bi bi-trash',
              command: () => this.deleteApplication(this.applicationDetails!.id),
              iconColor: 'icon-red',
            },
          ]
        : []),
      // {
      //   label: this.t.SPEEDDIAL_TOGGLE_COVER_LETTERS,
      //   icon: 'bi bi-file-text',
      //   command: () => this.toggleCoverLetterTable(),
      //   iconColor: 'icon-gray',
      // },
      // {
      //   label: this.t.SPEEDDIAL_TOGGLE_CURRICULUM_VITAE,
      //   icon: 'bi bi-file-earmark-person',
      //   command: () => this.toggleCurriculumVitaeTable(),
      //   iconColor: 'icon-gray',
      // },
      {
        label: this.t.SPEEDDIAL_LOG_FOLLOW_UP,
        icon: 'bi bi-reply-all',
        command: () => this.openFollowUpDialog(),
        iconColor: 'icon-blue',
      },
      {
        label: this.t.SPEEDDIAL_LOG_SALARY_MESSAGE,
        icon: 'bi bi-cash',
        command: () => this.openSalaryDialog(),
        iconColor: 'icon-green',
      },
      {
        label: this.t.SPEEDDIAL_MANAGE_INVITATIONS,
        icon: 'bi bi-calendar-event',
        command: () => this.openInvitationDialog(),
        iconColor: 'icon-purple',
      },
    ];
  }

  getStatusLabel(status: ApplicationStatus): string {
    switch (status) {
      case 'Pending':
        return this.t.STATUS_PENDING;
      case 'Submitted':
        return this.t.STATUS_SUBMITTED;
      case 'Invited':
        return this.t.STATUS_INVITED;
      case 'Interviewed':
        return this.t.STATUS_INTERVIEWED;
      case 'Rejected':
        return this.t.STATUS_REJECTED;
      case 'Hired':
        return this.t.STATUS_HIRED;
      case 'Canceled':
        return this.t.STATUS_CANCELED;
      case 'Obsolete':
        return this.t.STATUS_OBSOLETE;
      default:
        return status;
    }
  }

  getCurrentStatusTitle(status: ApplicationStatus): string {
    return this.interpolate(this.t.TOOLTIP_STATUS_CURRENT, {
      status: this.getStatusLabel(status),
    });
  }

  get formattedSalary() {
    if (!this.applicationDetails) return { min: '', target: '', max: '' };
    return {
      min: this.currencyFormat.format(
        this.applicationDetails.salary_min_value ?? 0,
        { abbreviate: true }
      ),
      target: this.currencyFormat.format(
        this.applicationDetails.salary_target_value ?? 0,
        { abbreviate: true }
      ),
      max: this.currencyFormat.format(
        this.applicationDetails.salary_max_value ?? 0,
        { abbreviate: true }
      ),
    };
  }

  get salaryFields() {
    return {
      salary_min_value: this.applicationDetails?.salary_min_value ?? null,
      salary_max_value: this.applicationDetails?.salary_max_value ?? null,
      salary_target_value: this.applicationDetails?.salary_target_value ?? null,
      salary_company_message:
        this.applicationDetails?.salary_company_message ?? '',
    };
  }

  getMapUrl(address: string): SafeResourceUrl {
    const url =
      'https://www.google.com/maps?q=' +
      encodeURIComponent(address) +
      '&output=embed';
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  getMapsLink(address: string): string {
    return environment.googleMapsSearchUrl + encodeURIComponent(address);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['application'] && this.application) {
      this.isLoading = false;
      this.error = null;
      this.applicationId = this.application.id;
      this.setApplicationDetails(this.application);
      this.cdr.markForCheck();
      return;
    }

    if (changes['applicationId'] && this.applicationId !== null) {
      this.fetchApplicationDetails();
    }

    // Re-fetch when refreshTrigger changes (parent signals data refresh)
    if (
      changes['refreshTrigger'] &&
      !changes['refreshTrigger'].firstChange &&
      this.applicationId !== null
    ) {
      this.fetchApplicationDetails();
    }
  }
  openWizardForThisApplication() {
    if (this.applicationId != null) {
      this.applicationService.setCurrentApplicationId(this.applicationId);
      this.jobApplicationWizardService.openWizard({ applicationId: this.applicationId });
    }
  }
  fetchApplicationDetails(): void {
    if (this.applicationId === null) {
      return;
    }

    if (this.application) {
      this.isLoading = false;
      this.error = null;
      this.setApplicationDetails(this.application);
      this.cdr.markForCheck();
      return;
    }

    this.isLoading = true;
    this.error = null;

    this.applicationService.getApplicationDetails(this.applicationId).subscribe(
      (data) => {
        this.setApplicationDetails(data);
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      (error) => {
        this.error = this.t.ERROR_FETCH_DETAILS;
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    );
  }

  toggleCoverLetterTable(): void {
    this.showCoverLetterTable = !this.showCoverLetterTable;
    this.cdr.markForCheck();
  }

  toggleCurriculumVitaeTable(): void {
    this.showCurriculumVitaeTable = !this.showCurriculumVitaeTable;
    this.cdr.markForCheck();
  }

  // Accept an application ID to delete specific application
  deleteApplication(applicationId: number): void {
    this.applicationService.deleteApplication(applicationId).subscribe(
      () => {
        console.log(
          `Application with ID ${applicationId} deleted successfully.`
        );
        this.messageService.add({
          severity: 'success',
          summary: this.t.TOAST_SUCCESS,
          detail: this.t.TOAST_APP_DELETED,
          life: 3000,
        });
      },
      (error) => {
        console.error('Error deleting application:', error);
      }
    );
  }

  // Update application status
  updateStatus(newStatus: string): void {
    if (this.applicationId === null) {
      return;
    }

    this.applicationService
      .updateApplicationStatus(
        this.applicationId,
        newStatus as ApplicationStatus
      )
      .subscribe(
        () => {
          if (this.applicationDetails) {
            this.applicationDetails.status = newStatus as ApplicationStatus;
          }
          this.cdr.markForCheck();
        },
        (error) => {
          this.error = this.t.ERROR_UPDATE_STATUS;
          console.error('Error updating status:', error);
        }
      );
  }

  openLatestCoverLetter(): void {
    // this.showCoverLetterTable = true;
    if (this.applicationId === null) {
      return;
    }
    this.coverLettersApiService
      .getApplicationCoverLetter(this.applicationId)
      .subscribe({
        next: (cl: CoverLetterResponse) => {
          // console.log('Latest CL:', cl);
          this.coverLetterService.setFullScreen(true);
          this.coverLetterService.processAndSetCoverLetter(cl);
        },
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: this.t.TOAST_ERROR,
            detail: this.t.TOAST_CL_FETCH_FAILED,
            life: 3000,
          });
        },
      });
    this.cdr.markForCheck();
  }

  openLatestCurriculumVitae(): void {
    // Replace with the actual application ID you want to use
    if (this.applicationId === null) {
      return;
    }
    const applicationId = this.applicationId; // or wherever you store it

    this.applicationService
      .getApplicationCurriculumVitae(applicationId)
      .subscribe({
        next: (cv: CurriculumVitaeResponse) => {
          this.applicationService.setCurrentApplicationId(applicationId);
          this.curriculumVitaeService.processAndSetCurriculumVitae(cv, true);
        },
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: this.t.TOAST_ERROR,
            detail: this.t.TOAST_CV_FETCH_FAILED,
            life: 3000,
          });
        },
      });
  }
  openJobPosition(): void {
    if (this.applicationId === null) return;
    this.jobPostingService.openJobPostingFullscreen(this.applicationId);
  }
}
